import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { ArrowRight, Check, ShieldCheck } from 'lucide-react'
import GlobalHeader from '@/components/GlobalHeader'
import GlobalFooter from '@/components/GlobalFooter'
import PublicViewToggle from '@/components/PublicViewToggle'
import HeroEntry from '@/components/home/HeroEntry'
import HubSpotEyebrow from '@/components/HubSpotEyebrow'
import { FEATURED_IMAGE, FEATURED_IMAGE_HEIGHT, FEATURED_IMAGE_WIDTH } from '@/content/siteImages'
import { BreakCrop, BreakRecord, ReadersCrop, ScoreCrop, WrittenCrop } from '@/components/home/crops'
import { useAppTheme } from '@/design-system/island'
import { Beside, Ground } from '@/design-system/ground'
import { ASKED, Inspector } from '@/design-system/inspector'
import { Cells, Feature, Lead, LogoWall } from '@/design-system/media'
import { Course, Lands, Named, Say, Says, Stat, Stats } from '@/design-system/market'
import { factsSection, heroContent, metadata, reviewsSection, testimonials } from '@/content/home'
import '@/design-system/home.css'

export default function HomePage1() {
  /* One module holds the title and description; the twin at /ai/home reads the same two. */
  const pageTitle = metadata.title
  const pageDescription = metadata.description
  const pageUrl = 'https://superschema.ai'
  const featuredImage = FEATURED_IMAGE

  const theme = useAppTheme()

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />

        {/* Open Graph */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={featuredImage} />
        <meta property="og:image:width" content={FEATURED_IMAGE_WIDTH} />
        <meta property="og:image:height" content={FEATURED_IMAGE_HEIGHT} />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={featuredImage} />

        {/* Canonical */}
        <link rel="canonical" href={pageUrl} />

        {/* AI-readable Alternate Formats */}
        <link rel="alternate" type="text/markdown" href="https://superschema.ai/content/home.md" />
        <link rel="alternate" type="text/plain" href="https://superschema.ai/content/home.txt" />

        {/* Organization Schema: the same identity as astro GlobalSchemas */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "@id": "https://superschema.ai/#organization",
          "name": "SuperSchema",
          "legalName": "Lightbulb Moment Labs, Inc.",
          "url": "https://superschema.ai",
          "logo": "https://superschema.ai/android-chrome-512x512.png",
          "description": pageDescription,
          "sameAs": [
            "https://www.linkedin.com/company/superschema/"
          ]
        })}</script>

        {/* WebSite Schema */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "@id": "https://superschema.ai/#website",
          "name": "SuperSchema",
          "url": "https://superschema.ai",
          "description": heroContent.headline,
          "publisher": {
            "@id": "https://superschema.ai/#organization"
          }
        })}</script>
      </Helmet>

      {/*
        A hand-rolled scope rather than DsRoot. DsRoot keeps its own theme in
        `ds.theme`, which is right for the isolated design-system routes and
        wrong here: the header's toggle writes `theme`, so the page body would
        have stopped following it. `ds-bleedclip` still has to be here, because
        the grounds below deliberately run off the viewport edge.
      */}
      <div className="ds ds-bleedclip" data-ds-theme={theme}>
        <GlobalHeader />

        <main>
          {/*
            The words and the scan control on paper, the picture beside them
            running off the right, and one exchange laid on the picture.

            The chart is the outcome this product is sold on, drawn instead
            of claimed: a buyer asks who to use and the answer names you.
            The question is a category, not a company, because a lead line
            naming the fictional business asks a reader to know who that is
            before the picture means anything.

            No numbers on it. It is marked as an example, which is the same
            licence the panel below this hero runs on, but a recommendation
            rate would be an invented statistic about an outcome rather than
            a labelled sample of the product's own output.
          */}
          <section className="ds-container" id="scan" style={{ paddingTop: 28, paddingBottom: 56 }}>
            <Beside
              photo="/design-system/grounds-1/ground-swirl-03.webp"
              height={520}
              eager
              over={
                <Named
                  ask={ASKED.ask}
                  you={ASKED.you}
                  others={ASKED.others}
                  note={ASKED.note}
                />
              }
            >
              <div style={{ maxWidth: 620 }}>
                <HubSpotEyebrow style={{ marginBottom: 10 }}>{heroContent.eyebrow}</HubSpotEyebrow>
                <h1 className="ds-display">{heroContent.headline}</h1>
                <p className="ds-lead" style={{ marginTop: 16 }}>
                  {heroContent.subheadline}
                </p>
                <div style={{ marginTop: 24 }}>
                  <HeroEntry placement="home_hero" startNote={heroContent.startNote} scanNote={heroContent.scanNote} />
                </div>
              </div>
            </Beside>
          </section>

          {/*
            What the software does, acted out on the left and said on the
            right: four things land on one page, in order, and the words
            beside them say the same thing once. The truth line is the one
            thing this product will not claim, and the deploy sentence is
            the same one /pricing and /products/deploy carry.
          */}
          <div className="ds-band" id="facts">
            <div className="ds-container ds-section">
              <Beside
                photo="/design-system/grounds-1/ground-swirl-18.webp"
                flip
                height={520}
                glass={{ from: 'top-right', second: 'bottom-left' }}
                over={
                  <Lands
                    label={factsSection.lands.label}
                    rows={factsSection.lands.rows.map((said, i) => ({
                      mark: i === factsSection.lands.rows.length - 1 ? <ShieldCheck aria-hidden="true" /> : <Check aria-hidden="true" />,
                      said,
                    }))}
                    note={factsSection.lands.note}
                  />
                }
              >
                <div style={{ maxWidth: 560 }}>
                  <h2 className="ds-h1">{factsSection.headline}</h2>
                  <p className="ds-lead" style={{ marginTop: 18 }}>{factsSection.body}</p>
                  <p style={{ marginTop: 24 }}>
                    <Link to={factsSection.linkUrl} className="ds-link ds-home__plans">
                      {factsSection.linkText}
                      <ArrowRight aria-hidden="true" />
                    </Link>
                  </p>
                </div>
              </Beside>
            </div>
          </div>

          {/*
            The signature, at a size where both halves are readable. It replaces
            a hand-built mockup that used three picsum.photos stock images and
            drew fake browser chrome around them.
          */}
          <section className="ds-container ds-section">
            <div className="ds-lift">
              <div className="ds-lift__ground">
                <Ground photo="/design-system/grounds-1/ground-swirl-05.webp" runs="left" />
                {/*
                  The glass lives in the picture, not around the card.

                  `Glass` wraps an object and offsets a sheet from its edges,
                  which is right where the object sits wholly on a
                  photograph. This card does not: the composition here is a
                  ground rising into it from below, so the top 42% of the
                  card is on paper. A sheet spanning the card would have been
                  a grey rectangle on white for nearly half its height.

                  So the sheets sit inside the ground instead, offset from
                  the card's right and bottom edges, which is where the
                  picture actually is. They are the same material: the pane
                  class is shared and only the geometry is local.
                */}
                <span className="ds-glass__pane ds-lift__glass" aria-hidden="true" />
                <span className="ds-glass__pane ds-glass__pane--2 ds-lift__glass2" aria-hidden="true" />
              </div>
              <div className="ds-lift__card">
                <Inspector showreel />
              </div>
            </div>
          </section>

          <section className="ds-container ds-tight">
            <LogoWall />
          </section>

          {/*
            Every number carries where it came from, in the block, in the
            reader's line of sight. `Stat` requires the source, so the type
            refuses a number without one.
          */}
          <section className="ds-band">
            <div className="ds-container ds-section">
              <div className="ds-narrow" style={{ marginBottom: 36 }}>
                <h2 className="ds-h1">Your customers already use AI every day.</h2>
                <p className="ds-lead" style={{ marginTop: 16 }}>
                  The customers worth winning are already in AI. The question is whether AI knows about you or about your competitor.
                </p>
              </div>
              <Stats>
                <Stat
                  num="51%"
                  said="of B2B customers start in AI"
                  more="Software customers now begin vendor research in AI chatbots, not Google."
                  from="G2 Answer Economy Report (n=1,076)"
                />
                <Stat
                  num="69%"
                  said="switched vendors because of AI"
                  more="B2B customers chose a different vendor than they planned, with 1 in 3 choosing a brand they had never heard of."
                  from="G2 Answer Economy Report"
                />
                <Stat
                  num="4.4x"
                  said="higher conversion from AI traffic"
                  more="Visitors arriving from AI convert 4.4 times better than organic search traffic."
                  from="HubSpot Spring 2026 (n=850)"
                />
              </Stats>
            </div>
          </section>

          <section className="ds-container ds-section">
            <div className="ds-narrow">
              <h2 className="ds-h1">Scan first. Audit the gaps. Monitor what matters.</h2>
              <p className="ds-lead" style={{ marginTop: 16 }}>
                AI does not just need to read your site. It needs to understand what you sell, who it is for, and what
                somebody should do next.
              </p>
            </div>
            <div style={{ marginTop: 36 }}>
              {/* Walking: scan, audit, monitor is an order, not a menu, and a
                  plain ruled list does not say so. */}
              <Course
                walk
                lessons={[
                  {
                    title: 'Scan',
                    line: 'A fast read on whether AI can understand the key facts on your website.',
                    long: 'About 30 seconds',
                  },
                  {
                    title: 'Audit',
                    line: 'The gaps in priority order, each one cited to the page it came from.',
                    long: 'A fix plan',
                  },
                  {
                    title: 'Monitor',
                    line: 'We watch for page changes and put back what a rebuild took away. Growth plans and above can also publish a SuperSchema Registry listing.',
                    long: 'Every night',
                  },
                ]}
                /*
                  What the three steps are for. A numbered list says what
                  happens and never says why any of it is worth doing.

                  It claims only what this product controls: what an
                  assistant can reach, whether it can read it, and whether it
                  stays that way. Not what an assistant will then say about
                  anybody, which is the promise the methodology page refuses
                  in writing, and no number.
                */
                ends={{
                  title: 'AI gets your business right',
                  line: 'What an assistant can find out about you comes from your own pages, in a form it can read, and it still does after the next redesign.',
                }}
              />
            </div>
          </section>

          <section className="ds-band ds-band--ink">
            <div className="ds-container ds-section">
              <div className="ds-narrow" style={{ marginBottom: 36 }}>
                <h2 className="ds-h1">Pages people love, that AI can also read.</h2>
                <p className="ds-lead" style={{ marginTop: 16 }}>
                  Most tools hand you a to-do list. SuperSchema does the work, then checks every night that it is still done.
                </p>
              </div>
              {/*
                A lead and three, not four of a kind. Four equal cells making
                four claims at one size gave a reader nothing to look at
                first, and three of the four pictures were horizontal bars,
                so the block read as one graphic repeated. The strongest
                claim now takes the width of the row and shows its picture
                whole; the other three support it underneath.
              */}
              <Cells cols={3} colsMd={3} colsSm={1} className="ds-cells--lead">
                <Lead
                  title="Nothing quietly breaks"
                  line="A page gets rebuilt, a plugin updates, and what AI was told about you disappears. We check every night and put it back."
                  foot={<BreakRecord />}
                >
                  <BreakCrop />
                </Lead>
                <Feature
                  small
                  title="Written from your own page"
                  line="Everything we publish is taken from what your site already says, and we never add what it does not."
                >
                  <WrittenCrop />
                </Feature>
                <Feature
                  small
                  title="See who has been reading your site"
                  line="Which AI agents visited, how often, and which of your pages they spent their time on."
                >
                  <ReadersCrop />
                </Feature>
                <Feature
                  small
                  title="Know where you stand"
                  line="One score for how much of your business AI can understand, and the clearest next thing to do about it."
                >
                  <ScoreCrop />
                </Feature>
              </Cells>
            </div>
          </section>

          {/*
            Their words, not ours. Every one of these was written by a
            customer: the heading of each card is the reviewer's own title for
            their review, kept whole, including the parts asking us for things
            we have not built. Nothing is rated, because the ratings on this
            product have been invented before and never will be again.
          */}
          <section className="ds-container ds-section">
            <Says title={reviewsSection.title}>
              {testimonials.map((t) => (
                <Say key={t.who} head={t.head} said={t.said} who={t.who} />
              ))}
            </Says>
          </section>

          {/*
            The one full-colour moment. Everything above is paper, ink and
            photographs, so the page ends on the accent itself.
          */}
          <div className="ds-band--accent">
            <div className="ds-container ds-section">
              <div className="ds-narrow" style={{ margin: '0 auto', textAlign: 'center' }}>
                <h2 className="ds-h1">SuperSchema makes sure AI gets your business right.</h2>
                <p className="ds-lead" style={{ marginTop: 16 }}>
                  We set it up, check it every night, and put it back when your site changes.
                </p>
              </div>
              <div style={{ maxWidth: 620, margin: '28px auto 0' }}>
                <HeroEntry placement="home_closing" startNote={heroContent.startNote} scanNote={heroContent.scanNote} onBand />
              </div>
            </div>
          </div>
        </main>

        <GlobalFooter />
        <PublicViewToggle currentView="human" humanUrl="/" robotUrl="/ai/home" />
      </div>
    </>
  )
}
